<script setup lang="ts">
import { ref, onMounted, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import type { TournamentRoster } from "@interfaces/tournament_type";
import { formatRosterToText } from "./util/RosterFormatter";
import { useLocalizedNamesStore } from "../../stores/localizedNames";

interface Props {
  roster: TournamentRoster | null;
  isVisible: boolean;
}

const props = defineProps<Props>();
const emit = defineEmits<{
  close: [];
}>();

const { t } = useI18n();
const namesStore = useLocalizedNamesStore();
const formattedText = ref<string>('');

const closeModal = () => {
  emit('close');
};

const handleOverlayClick = (event: Event) => {
  if (event.target === event.currentTarget) {
    closeModal();
  }
};

const updateFormattedText = async () => {
  if (props.roster) {
    formattedText.value = await formatRosterToText(props.roster, namesStore);
  }
};

onMounted(() => {
  updateFormattedText();
});

watch(() => props.roster, () => {
  updateFormattedText();
}, { deep: true });

watch(() => namesStore.locale, () => {
  updateFormattedText();
});
</script>

<template>
  <div v-if="isVisible && roster" class="modal-overlay" @click="handleOverlayClick">
    <div class="modal-content" @click.stop>
      <div class="modal-header">
        <h2>{{ t('roster_details.title') }}</h2>
        <button class="close-button" @click="closeModal">
          <i class="bi bi-x"></i>
        </button>
      </div>

      <div class="modal-body">
        <div class="roster-info">
          <div class="roster-header">
            <h3 class="roster-hero">{{ roster.hero_name || t('roster_details.hero_not_specified') }}</h3>
            <div class="roster-faction">{{ roster.faction_name }}</div>
          </div>

          <div v-if="roster.points_overall || roster.points_left" class="roster-points">
            <div v-if="roster.points_overall" class="points-item">
              <span class="points-label">{{ t('roster_details.total_cost') }}:</span>
              <span class="points-value">{{ roster.points_overall }}</span>
            </div>
            <div v-if="roster.points_left" class="points-item">
              <span class="points-label">{{ t('roster_details.points_left') }}:</span>
              <span class="points-value">{{ roster.points_left }}</span>
            </div>
          </div>

          <div v-if="roster.structure" class="roster-structure">
            <h4>{{ t('roster_details.structure_title') }}</h4>
            <pre class="structure-content">{{ formattedText }}</pre>
          </div>

          <div v-else class="no-structure">
            <p>{{ t('roster_details.structure_unavailable') }}</p>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 20px;
}

.modal-content {
  background-color: #efd8b0;
  border-radius: 8px;
  border: 1px solid #bd9c65;
  max-width: 600px;
  width: 100%;
  max-height: 90vh;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px;
  border-bottom: 1px solid #bd9c65;
  background-color: #c29c58;
  color: #60451f;
}

.modal-header h2 {
  margin: 0;
  font-size: 20px;
  font-weight: bold;
}

.close-button {
  background: none;
  border: none;
  color: #60451f;
  font-size: 24px;
  cursor: pointer;
  padding: 4px;
  border-radius: 4px;
  transition: all 0.2s ease;

  &:hover {
    background-color: rgba(96, 69, 31, 0.1);
    transform: scale(1.1);
  }
}

.modal-body {
  padding: 20px;
  overflow-y: auto;
  flex: 1;
}

.roster-info {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.roster-header {
  text-align: center;
  padding-bottom: 15px;
  border-bottom: 1px solid #bd9c65;
}

.roster-name {
  margin: 0 0 8px 0;
  color: #60451f;
  font-size: 24px;
  font-weight: bold;
}

.roster-faction {
  color: #bd9c65;
  font-size: 16px;
  font-weight: 500;
}

.roster-points {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 15px;
  background-color: rgba(194, 156, 88, 0.1);
  border-radius: 6px;
  border: 1px solid #bd9c65;
}

.points-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.points-label {
  color: #60451f;
  font-weight: 500;
}

.points-value {
  color: #60451f;
  font-weight: bold;
  font-size: 16px;
}

.roster-structure {
  h4 {
    margin: 0 0 15px 0;
    color: #60451f;
    font-size: 18px;
    font-weight: bold;
  }
}

.structure-content {
  margin: 0;
  padding: 15px;
  background-color: #f8f9fa;
  color: #333;
  font-size: 14px;
  line-height: 1.6;
  font-family: 'Arial', sans-serif;
  white-space: pre-wrap;
  word-wrap: break-word;
  max-height: 400px;
  overflow-y: auto;
  border-radius: 6px;
  border: 1px solid #dee2e6;
}

.no-structure {
  text-align: center;
  padding: 20px;
  color: #bd9c65;
  font-style: italic;
}

// Responsive design
@media (max-width: 768px) {
  .modal-overlay {
    padding: 10px;
  }

  .modal-content {
    max-width: 100%;
    max-height: 95vh;
  }

  .modal-header {
    padding: 15px;
  }

  .modal-body {
    padding: 15px;
  }

  .roster-name {
    font-size: 20px;
  }

  .structure-content {
    font-size: 13px;
    padding: 12px;
  }
}
</style>
